// Copyright (c) 2020 WiseTime. All rights reserved.

import { Action, createAction } from "@reduxjs/toolkit"
import {
  AddToTimeGroupRequest,
  AttachTagRequest,
  CancelPostTimeRequest,
  ClearTimeGroupErrorsRequest,
  ClearTimesheetFieldRequest,
  CreateEmptyTimeGroupRequest,
  CreateTimeGroupRequest,
  DeclineTagSuggestionRequest,
  DeleteTimeGroupRequest,
  DeleteTimelineMarkerRequest,
  DetachTagCategoryRequest,
  DetachTagRequest,
  EmptyTrashRequest,
  ImmediatelyDeleteTimeRowsRequest,
  ListDaySummariesRequest,
  ListTimeRowsByDateRequest,
  MergeTimeGroupsRequest,
  RemoveFromTimeGroupRequest,
  SelectTimesheetFieldOptionRequest,
  SetActivityTypeRequest,
  TimeGroup,
  TrashTimeRowsRequest,
  UnsetActivityTypeRequest,
  UnTrashTimeRowsRequest,
  UpdateTimeGroupDurationRequest,
  UpdateTimeGroupRequest,
} from "../../generated/wisetime/user/timeline/v4/timeline_pb"
import { Date } from "../../generated/google/type/date_pb"
import { TagRef } from "../../generated/wisetime/user/type/tag_ref_pb"
import { SiteRef } from "../../generated/wisetime/type/site_ref_pb"
import { ActivityTypeRef } from "../../generated/wisetime/user/type/activity_type_ref_pb"
import {
  CreateLeaveRequest,
  DeleteLeaveRequest,
  ListLeavesRequest,
  ListMemberExpectationsRequest,
} from "../../generated/legebill/site/postexpectation/v4/post_expectation_pb"
import { TimeZone } from "../../app/timeZone"
import {
  BatchTranslateTextsRequest,
  GenerateNarrativeRequest,
} from "../../generated/wisetime/user/assistant/v4/assistant_pb"
import { TimelineDate } from "./dateTime"
import {
  AddFavouriteTimesheetFieldOptionRequest,
  BatchGetTimesheetFieldConfigsRequest,
  BatchGetTimesheetFieldOptionsRequest,
  ListTimesheetFieldsRequest,
  RemoveFavouriteTimesheetFieldOptionRequest,
  ReorderFavouriteTimesheetFieldOptionRequest,
} from "../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type AttachTagPayload = {
  attachTagRequest: AttachTagRequest,
  tag: TagRef,
}

type ReplaceTagPayload = {
  attachTagRequest: AttachTagRequest,
  detachTagRequest: DetachTagRequest,
  tag: TagRef,
  onError: () => void,
}

type SetActivityTypePayload = {
  setActivityTypeRequest: SetActivityTypeRequest,
  activityType: ActivityTypeRef,
}

export type PostTimePayload = {
  timeGroups: TimeGroup.AsObject[],
  currentSiteRef: SiteRef.AsObject,
  roundingPreferenceSec: number,
}

type EmptyTrashPayload = {
  request: EmptyTrashRequest,
  onComplete: () => void,
}

export type DeleteLeavePayload = {
  deleteLeaveRequest: DeleteLeaveRequest,
  membershipId: string,
  date: Date.AsObject,
}

export const getTimelineData = createAction<TimelineDate | undefined>("timelines/get-timeline-data")
export const getCurrentTimelineData = (): Action => getTimelineData()

export const listTimesheetFields = createAction<ListTimesheetFieldsRequest>("timelines/list-timesheet-fields")
export const listFavouriteTimesheetFieldOptions = createAction("timelines/list-favourite-timesheet-field-options")
export const listRecentTimesheetFieldOptions = createAction("timelines/list-recent-timesheet-field-options")
export const generateTimesheetFieldConfigs = createAction<TimeGroup.AsObject[]>("timelines/generate-timesheet-field-configs")
export const batchGetTimesheetFieldConfigs = createAction<BatchGetTimesheetFieldConfigsRequest>("timelines/batch-get-timesheet-field-configs")
export const batchGetTimesheetFieldOptions = createAction<BatchGetTimesheetFieldOptionsRequest>("timelines/batch-get-timesheet-field-options")
export const selectTimesheetFieldOption = createAction<SelectTimesheetFieldOptionRequest>("timelines/select-timesheet-field-option")
export const clearTimesheetField = createAction<ClearTimesheetFieldRequest>("timelines/clear-timesheet-field")
export const addFavouriteTimesheetFieldOption = createAction<AddFavouriteTimesheetFieldOptionRequest>("timelines/add-favourite-timesheet-field-option")
export const removeFavouriteTimesheetFieldOption = createAction<RemoveFavouriteTimesheetFieldOptionRequest>("timelines/remove-favourite-timesheet-field-option")
export const reorderFavouriteTimesheetFieldOption = createAction<ReorderFavouriteTimesheetFieldOptionRequest>("timelines/reorder-favourite-timesheet-field-option")

export const watchTimelineUpdates = createAction("timelines/watch-timeline-updates")
export const listDaySummaries = createAction<ListDaySummariesRequest>("timelines/list-day-summaries")

export const setTimeZone = createAction<TimeZone>("timelines/set-time-zone")

// Dispatched whenever we faced that timeline looks to be out of sync with the server
export const onTimelineOutOfSync = createAction("timelines/out-of-sync")
export const syncCurrentDay = createAction("timelines/on-sync-current-day")

export const attachTag = createAction<AttachTagPayload>("timelines/attach-tag")
export const replaceTag = createAction<ReplaceTagPayload>("timelines/replace-tag")
export const setActivityType = createAction<SetActivityTypePayload>("timelines/set-activity-type")
export const unsetActivityType = createAction<UnsetActivityTypeRequest>("timelines/unset-activity-type")

export const declineTagSuggestion = createAction<DeclineTagSuggestionRequest>("timelines/decline-tag-suggestion")

export type CreateTimeGroupPayload = {
  request: CreateTimeGroupRequest,
  skipSelection?: boolean,
  post?: boolean,
}
export const createTimeGroup = createAction<CreateTimeGroupPayload>("timelines/create-time-group")
export const postTimeGroup = createAction<TimeGroup.AsObject>("timelines/post-time-group")

// Add time row ids to time group, or remove time row ids from time group
// using a FieldMask with the latest time row ids
export const updateTimeGroup = createAction<UpdateTimeGroupRequest>("timelines/update-time-group")

export const updateTimeGroupDuration = createAction<UpdateTimeGroupDurationRequest>("timelines/update-time-group-duration")

export type CreateEmptyTimeGroupPayload = {
  request: CreateEmptyTimeGroupRequest,
  skipSelection?: boolean,
  createTimeGroupRowIndex?: number,
  onSuccess?: () => void,
  onError?: () => void,
}
export const createEmptyTimeGroup = createAction<CreateEmptyTimeGroupPayload>("timelines/create-empty-time-group")

export const addToTimeGroup = createAction<AddToTimeGroupRequest>("timelines/add-to-time-group")
export const removeFromTimeGroup = createAction<RemoveFromTimeGroupRequest>("timelines/remove-from-time-group")

export const mergeTimeGroups = createAction<MergeTimeGroupsRequest>("timelines/merge-time-groups")
export const mergeTimeGroupsFulfilled = createAction("timelines/merge-time-groups-fulfilled")
export const mergeTimeGroupsErrored = createAction("timelines/merge-time-groups-errored")
export const deleteTimeGroups = createAction<{ requests: DeleteTimeGroupRequest[], selectedTimeRowsIds: string[] }>("timelines/delete-time-groups")

export const trashTimeRows = createAction<TrashTimeRowsRequest>("timelines/trash-time-rows")
export const untrashTimeRows = createAction<UnTrashTimeRowsRequest>("timelines/untrash-time-rows")
export const immediatelyDeleteTimeRows = createAction<ImmediatelyDeleteTimeRowsRequest>("timelines/immediately-delete-time-rows")

export const postTime = createAction<PostTimePayload>("timelines/postTime")
export const cancelPostTime = createAction<CancelPostTimeRequest>("timelines/cancelPostTime")

export const emptyTrash = createAction<EmptyTrashPayload>("timelines/empty-trash")
export const detachTag = createAction<DetachTagRequest>("timelines/detach-tag")

export const listTimeRowsByDate = createAction<ListTimeRowsByDateRequest>("timelines/list-time-rows-by-date")

export const clearTimeGroupErrors = createAction<ClearTimeGroupErrorsRequest>("timelines/clear-time-group-errors")

export const detachTagCategory = createAction<DetachTagCategoryRequest>("timelines/detach-tag-category")

export const warnMultipleTagsPersisted = createAction("timelines/warn-multiple-tags-persisted")
export const warnMultipleTagsPersistFailed = createAction("timelines/warn-multiple-tags-persist-failed")
export const loadWarnMultipleTagsFromStorage = createAction("timelines/load-warn-multiple-tags-from-storage")
export const loadWarnMultipleTagsFromStorageFailed = createAction("timelines/load-warn-multiple-tags-from-storage-failed")

export const actionPaneNoteIsHiddenPersisted = createAction("timelines/action-pane-note-is-hidden-persisted")
export const actionPaneNoteIsHiddenPersistFailed = createAction("timelines/action-pane-note-is-hidden-persist-failed")
export const loadActionPaneNoteIsHiddenFromStorage = createAction("timelines/load-action-pane-note-is-hidden-from-storage")
export const loadActionPaneNoteIsHiddenFromStorageFailed = createAction("timelines/load-action-pane-note-is-hidden-from-storage-failed")

export const tilesViewTypePersisted = createAction("timelines/tiles-view-type-persisted")
export const tilesViewTypePersistFailed = createAction("timelines/tiles-view-type-persist-failed")
export const loadTilesViewTypeFromStorage = createAction("timelines/load-tiles-view-type-from-storage")
export const loadTilesViewTypeFromStorageFailed = createAction("timelines/load-tiles-view-type-from-storage-failed")

export const tagsViewTypePersisted = createAction("timelines/tags-view-type-persisted")
export const tagsViewTypePersistFailed = createAction("timelines/tags-view-type-persist-failed")
export const loadTagsViewTypeFromStorage = createAction("timelines/load-tags-view-type-from-storage")
export const loadTagsViewTypeFromStorageFailed = createAction("timelines/load-tags-view-type-from-storage-failed")

export const timesheetFieldsViewTypePersisted = createAction("timelines/timesheet-fields-view-type-persisted")
export const timesheetFieldsViewTypePersistFailed = createAction("timelines/timesheet-fields-view-type-persist-failed")
export const loadTimesheetFieldsViewTypeFromStorage = createAction("timelines/load-timesheet-fields-view-type-from-storage")
export const loadTimesheetFieldsViewTypeFromStorageFailed = createAction("timelines/load-timesheet-fields-view-type-from-storage-failed")

export const autoNarrativePersisted = createAction("timelines/auto-narrative-persisted")
export const autoNarrativePersistFailed = createAction("timelines/auto-narrative-persist-failed")
export const loadAutoNarrativeFromStorage = createAction("timelines/load-auto-narrative-from-storage")
export const loadAutoNarrativeFromStorageFailed = createAction("timelines/load-auto-narrative-from-storage-failed")

export const timeGroupsDisplayModePersisted = createAction("timelines/time-groups-display-mode-persisted")
export const timeGroupsDisplayModePersistFailed = createAction("timelines/time-groups-display-mode-persist-failed")
export const loadTimeGroupsDisplayModeFromStorage = createAction("timelines/load-time-groups-display-mode-from-storage")
export const loadTimeGroupsDisplayModeFromStorageFailed = createAction("timelines/load-time-groups-display-mode-from-storage-failed")

export const emptyTrashDoNotAskAgainPersisted = createAction("timelines/empty-trash-do-not-ask-again-persisted")
export const emptyTrashDoNotAskAgainPersistFailed = createAction("timelines/empty-trash-do-not-ask-again-persist-failed")
export const loadEmptyTrashDoNotAskAgainFromStorage = createAction("timelines/load-empty-trash-do-not-ask-again-from-storage")
export const loadEmptyTrashDoNotAskAgainFromStorageFailed = createAction("timelines/load-empty-trash-do-not-ask-again-from-storage-failed")

export const timesheetFieldViewModePersisted = createAction("timelines/timesheet-field-view-mode-persisted")
export const timesheetFieldViewModePersistFailed = createAction("timelines/timesheet-field-view-mode-persist-failed")
export const loadTimesheetFieldViewModeFromStorage = createAction("timelines/load-timesheet-field-view-mode-from-storage")
export const loadTimesheetFieldViewModeFromStorageFailed = createAction("timelines/load-timesheet-field-view-mode-from-storage-failed")

export const listLeaves = createAction<ListLeavesRequest>("timelines/list-leaves")
export const listLeavesErrored = createAction("timelines/list-leaves-errored")
export const createLeave = createAction<CreateLeaveRequest>("timelines/create-leave")
export const deleteLeave = createAction<DeleteLeavePayload>("timelines/delete-leave")
export const listMemberExpectations = createAction<ListMemberExpectationsRequest>("timelines/list-member-expectations")
export const refreshMemberExpectations = createAction<ListMemberExpectationsRequest>("timelines/refresh-member-expectations")

export const generateNarrative = createAction<GenerateNarrativeRequest>("timelines/generate-narrative")
export const translateNarratives = createAction<BatchTranslateTextsRequest>("timelines/translate-narratives")

export const deleteTimelineMarker = createAction<DeleteTimelineMarkerRequest>("timelines/delete-timeline-marker")

export const checkPendingPostStatus = createAction("timelines/check-pending-post-status")

export const refreshTimesheetFieldConfigs = createAction("timelines/refresh-timesheet-field-configs")

export const periodicRefreshTimesheetFieldConfigs = createAction("timelines/periodic-refresh-timesheet-field-configs")
